// src/main.js
// Bootstrap del CRM (Vite + módulos) manteniendo compatibilidad con el MVP legacy.
// Objetivos en esta versión:
// - Inicializar Tailwind CDN + estilos legacy mínimos.
// - Exponer librerías como globals si el legacy las espera (Chart, XLSX, jsPDF).
// - Inicializar Supabase en window.__supabase (compatibilidad).
// - Inicializar Store (state) + Router (router).
// - Cargar templates HTML (Toasts + Modals) e inyectarlos en #toasts-root / #modals-root.
// - Conectar Toasts del store a render DOM (mínimo funcional).
// - Exponer utilidades globales NO intrusivas (window.CRM) para depuración.

import { initStore, subscribe, getState, actions, bus, setState } from "./app/state.js";
import { initRouter, mountRoute, refreshActiveNav, isMobileViewport } from "./app/router.js";
import "./styles/main.css";
import { getSupabaseClient, onAuthStateChange } from "./services/supabaseClient.js";
import { getCurrentTeamId, getProfileByUserId, getCurrentTeamMemberId } from "./services/teamService.js";
import { getEventIdsByMember } from "./services/lineupService.js";
import { clearRoleCache, getUserRole } from "./services/rbac.js";
import { listAllEvents } from "./services/eventsService.js";
import { createEventExpense } from "./services/expensesService.js";
import { searchContacts } from "./services/contactsService.js";
import { listAllEmailsWithDetails } from "./services/emailsService.js";
import EventModal from "./ui/components/EventModal.js";
import ActivityModal from "./ui/components/ActivityModal.js";
import ExpenseModal from "./ui/components/ExpenseModal.js";
import PaymentModal from "./ui/components/PaymentModal.js";
import ContactsModal from "./ui/components/ContactsModal.js";
import { openUnavailabilityModal } from "./ui/components/UnavailabilityModal.js";
import { openThreadOnLoad } from "./ui/pages/Emailing.js";
import { mountUserMenu } from "./ui/components/UserMenu.js";
import { normalizeSearch } from "./utils/stringUtils.js";
import { onSafeBackdropClick } from "./ui/modalUtils.js";

const { openCreateEventModal, openEditEventModal } = EventModal;
const { openCreateActivityModal } = ActivityModal;
const { openCreateEventExpenseModal } = ExpenseModal;
const { openCreatePaymentModal } = PaymentModal;
const { openEditContactModal } = ContactsModal;

// Estilos globales (para que Vite los incluya en el bundle)
import "@fortawesome/fontawesome-free/css/all.min.css";

// =========================
// 0) Helpers
// =========================
const DEBUG_BOOT = true;

function log(...args) {
  if (DEBUG_BOOT) console.log("[boot]", ...args);
}
function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}
function warn(...args) {
  console.warn("[boot]", ...args);
}

function ensureEl(selector, name = selector) {
  const el = document.querySelector(selector);
  if (!el) throw new Error(`No existe ${name} (${selector}) en el DOM.`);
  return el;
}

async function loadScript(src, { async = true, defer = true } = {}) {
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src;
    s.async = async;
    s.defer = defer;
    s.onload = () => resolve(true);
    s.onerror = (e) => reject(e);
    document.head.appendChild(s);
  });
}

// Templates con ?raw para que Vite los incruste como string en el build
import toastTpl from "./ui/components/Toasts.html?raw";
import eventModalTpl from "./ui/components/EventModal.html?raw";
import contactModalTpl from "./ui/components/ContactsModal.html?raw";
import venueModalTpl from "./ui/components/VenueModal.html?raw";
import activityModalTpl from "./ui/components/ActivityModal.html?raw";
import userMenuTpl from "./ui/components/UserMenu.html?raw";
import userManagementTpl from "./ui/components/UserManagementModal.html?raw";

function injectStyle(cssText, id) {
  const exists = id ? document.getElementById(id) : null;
  if (exists) return;
  const style = document.createElement("style");
  if (id) style.id = id;
  style.textContent = cssText;
  document.head.appendChild(style);
}

// =========================
// 1) Tailwind (CDN) + estilos legacy mínimos
// =========================
async function initTailwindCdn() {
  if (window.tailwind || document.querySelector('script[src*="cdn.tailwindcss.com"]')) {
    log("Tailwind ya estaba presente.");
    return;
  }
  window.tailwind = window.tailwind || {};
  window.tailwind.config = {
    theme: {
      extend: {
        colors: {
          primary: "#f774ef",
          secondary: "#10B981",
          dark: "#9e1147",
          light: "#dce5f7",
        },
      },
    },
  };
  injectStyle(
    `
    [x-cloak]{ display:none !important; }
    .custom-scrollbar::-webkit-scrollbar{ width:6px; height:6px; }
    .custom-scrollbar::-webkit-scrollbar-track{ background:#f1f1f1; }
    .custom-scrollbar::-webkit-scrollbar-thumb{ background:#c1c1c1; border-radius:10px; }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover{ background:#a8a8a8; }
    .dragging{ opacity:0.5; transform:scale(0.95); box-shadow:0 10px 25px -5px rgba(0,0,0,0.2); }
    .drag-over{ background-color: rgba(79,70,229,0.15) !important; border: 2px dashed #4F46E5 !important; }
    .funnel-card{ transition: transform .2s ease, box-shadow .2s ease, opacity .2s ease; }
    .funnel-card:hover{ transform: translateY(-2px); }
    .funnel-column{ transition: background-color .2s ease, border .2s ease; }
    .toast{ min-width:260px; max-width:420px; background:#fff; border:1px solid rgba(15,23,42,.10); border-radius:16px; padding:10px 12px; box-shadow:0 12px 30px rgba(15,23,42,.12); font-size:13px; line-height:1.35; }
    .toast--success{ border-color: rgba(16,185,129,0.35); }
    .toast--error{ border-color: rgba(239,68,68,0.35); }
    .toast--info{ border-color: rgba(59,130,246,0.35); }
  `,
    "legacy-min-styles"
  );
  await loadScript("https://cdn.tailwindcss.com", { async: true, defer: false });
  log("Tailwind CDN cargado.");
}

async function initGlobalsLibs() {
  try {
    const mod = await import("chart.js/auto");
    window.Chart = mod.default || mod;
    log("Chart.js listo (window.Chart).");
  } catch (e) {
    warn("No se pudo inicializar Chart.js como global.", e);
  }
  try {
    const XLSX = await import("xlsx");
    window.XLSX = XLSX;
    log("XLSX listo (window.XLSX).");
  } catch (e) {
    warn("No se pudo inicializar XLSX como global.", e);
  }
  try {
    const { jsPDF } = await import("jspdf");
    window.jspdf = window.jspdf || {};
    window.jspdf.jsPDF = jsPDF;
    log("jsPDF listo (window.jspdf.jsPDF).");
  } catch (e) {
    warn("No se pudo inicializar jsPDF como global.", e);
  }
}

function initSupabaseGlobalSafe() {
  try {
    const client = getSupabaseClient();
    log("Supabase listo (window.__supabase).");
    return client;
  } catch (e) {
    warn("Supabase NO inicializado (faltan envs o error).", e);
    return null;
  }
}

async function loadAndInjectTemplates() {
  const modalsRoot = ensureEl("#modals-root", "#modals-root");
  const toastsRoot = ensureEl("#toasts-root", "#toasts-root");
  ensureTemplateInjected(toastsRoot, "toast-template", toastTpl);
  ensureTemplateInjected(modalsRoot, "event-modal-template", eventModalTpl);
  ensureTemplateInjected(modalsRoot, "contacts-modal-template", contactModalTpl);
  ensureTemplateInjected(modalsRoot, "venue-modal-template", venueModalTpl);
  ensureTemplateInjected(modalsRoot, "activity-modal-template", activityModalTpl);
  ensureTemplateInjected(document.body, "user-menu-template", userMenuTpl);
  ensureTemplateInjected(document.body, "user-management-modal-template", userManagementTpl);
  log("Templates UI inyectados.");
}

function ensureTemplateInjected(containerEl, templateId, htmlText) {
  if (document.getElementById(templateId)) return;
  const wrapper = document.createElement("div");
  wrapper.innerHTML = htmlText.trim();
  const tpl = wrapper.querySelector(`template#${CSS.escape(templateId)}`);
  if (!tpl) throw new Error(`No se encontró template#${templateId} en el HTML inyectado.`);
  containerEl.appendChild(tpl);
}

function mountToastsRenderer() {
  const root = ensureEl("#toasts-root", "#toasts-root");
  const tpl = ensureEl("#toast-template", "#toast-template");
  const render = () => {
    const st = getState();
    const toasts = st?.ui?.toasts || [];
    const existing = new Map();
    root.querySelectorAll("[data-toast-id]").forEach((el) => existing.set(el.getAttribute("data-toast-id"), el));
    for (const t of toasts) {
      if (!t?.id) continue;
      let el = existing.get(t.id);
      if (!el) {
        el = cloneToastFromTemplate(tpl, t);
        el.setAttribute("data-toast-id", t.id);
        root.appendChild(el);
      } else {
        updateToastEl(el, t);
        existing.delete(t.id);
      }
    }
    for (const [, el] of existing) el.remove();
  };
  subscribe(() => render());
  root.addEventListener("click", (ev) => {
    const btn = ev.target.closest(".toast-close");
    if (!btn) return;
    const toastEl = btn.closest("[data-toast-id]");
    if (!toastEl) return;
    const id = toastEl.getAttribute("data-toast-id");
    if (id) actions.removeToast(id);
  });
  render();
  log("Toasts renderer montado.");
}

function cloneToastFromTemplate(templateEl, toast) {
  const frag = templateEl.content.cloneNode(true);
  const wrapper = document.createElement("div");
  wrapper.appendChild(frag);
  const el = wrapper.querySelector(".toast");
  if (!el) throw new Error("Template toast inválido: no existe .toast root.");
  updateToastEl(el, toast);
  return el;
}

function updateToastEl(el, toast) {
  const type = toast.type || "info";
  el.classList.remove("toast--success", "toast--error", "toast--info");
  if (type === "success") el.classList.add("toast--success");
  else if (type === "error") el.classList.add("toast--error");
  else el.classList.add("toast--info");
  const iconEl = el.querySelector(".toast-icon i");
  if (iconEl) iconEl.className = "fa-solid " + (type === "success" ? "fa-circle-check" : type === "error" ? "fa-triangle-exclamation" : "fa-circle-info");
  const titleEl = el.querySelector(".toast-title");
  const msgEl = el.querySelector(".toast-message");
  if (titleEl) {
    if (toast.title) { titleEl.textContent = String(toast.title); titleEl.classList.remove("hidden"); }
    else { titleEl.textContent = ""; titleEl.classList.add("hidden"); }
  }
  if (msgEl) msgEl.textContent = String(toast.message || "");
}

function mountModalHelpers() {
  const modalsRoot = ensureEl("#modals-root", "#modals-root");
  function openModal(templateId) {
    // 1. Destroy any existing instance of the SAME modal type to prevent duplicate stacking
    const existing = modalsRoot.querySelector(`[data-modal-instance="${templateId}"]`);
    if (existing) {
      existing.remove();
      console.warn(`[Modals] Prev instance of ${templateId} destroyed to prevent stacking.`);
    }

    const tpl = document.getElementById(templateId);
    if (!tpl) throw new Error(`No existe template #${templateId}.`);
    const node = tpl.content.cloneNode(true);
    const wrapper = document.createElement("div");
    wrapper.appendChild(node);
    const el = wrapper.firstElementChild;
    if (!el) throw new Error(`Template ${templateId} no tiene root element.`);
    el.setAttribute("data-modal-instance", templateId);
    onSafeBackdropClick(el, () => el.remove());
    el.addEventListener("click", (ev) => {
      const closeBtn = ev.target.closest("#event-modal-close, #event-modal-cancel, #contact-modal-close, #contact-modal-cancel, #venue-modal-close, #venue-modal-cancel, #activity-modal-close, #activity-modal-cancel, #profile-modal-close, #profile-modal-cancel, #user-management-modal-close");
      if (!closeBtn) return;
      ev.stopPropagation(); el.remove();
    });
    modalsRoot.appendChild(el);
    return el;
  }
  function closeAllModals() { modalsRoot.querySelectorAll("[data-modal-instance]").forEach((n) => n.remove()); }
  function closeModal(el) { if (el && el.remove) el.remove(); }
  window.CRM = window.CRM || {};
  window.CRM.modals = { openModal, closeAllModals, closeModal };
}

function mountSidebarToggle() {
  const sidebar = document.getElementById("main-sidebar");
  const toggle = document.getElementById("sidebar-toggle");
  if (!sidebar || !toggle) return;
  if (localStorage.getItem("crm-sidebar-collapsed") !== "false") sidebar.classList.add("is-collapsed");
  toggle.addEventListener("click", () => {
    sidebar.classList.toggle("is-collapsed");
    localStorage.setItem("crm-sidebar-collapsed", sidebar.classList.contains("is-collapsed"));
  });
}

// =========================
// 7.5) PWA (solo móvil): "Añadir a pantalla de inicio", sin soporte offline.
// En escritorio no se registra manifest ni service worker: no interesa que
// el navegador ofrezca instalar la app fuera del móvil.
// =========================
function mountPwaForMobile() {
  if (!isMobileViewport()) return;

  const manifestLink = document.createElement("link");
  manifestLink.rel = "manifest";
  manifestLink.href = "/manifest.json";
  document.head.appendChild(manifestLink);

  const appleTouchIcon = document.createElement("link");
  appleTouchIcon.rel = "apple-touch-icon";
  appleTouchIcon.href = "/icons/apple-touch-icon.png";
  document.head.appendChild(appleTouchIcon);

  // Meta tags para que iOS/Safari abra en modo standalone al añadir a inicio.
  const appleCapable = document.createElement("meta");
  appleCapable.name = "apple-mobile-web-app-capable";
  appleCapable.content = "yes";
  document.head.appendChild(appleCapable);

  const appleStatusBar = document.createElement("meta");
  appleStatusBar.name = "apple-mobile-web-app-status-bar-style";
  appleStatusBar.content = "black-translucent";
  document.head.appendChild(appleStatusBar);

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.register("/sw.js").catch((e) => warn("No se pudo registrar el service worker.", e));
  }
}

// =========================
// 8) Mobile Quick Actions logic
// =========================
function mountQuickActions() {
  const overlay = document.getElementById("quick-action-overlay");
  const plusBtn = document.getElementById("mobile-quick-expense");
  const closeBtn = document.getElementById("close-quick-actions");
  const searchOverlay = document.getElementById("event-search-overlay");
  const searchClose = document.getElementById("close-event-search");
  const searchInput = document.getElementById("mobile-event-search-input");
  const searchResults = document.getElementById("mobile-event-search-results");

  if (!overlay || !plusBtn) return;

  let currentAction = null;

  // Mismo rol que el router: sin perfil, desactivado o desconocido = sin acciones.
  async function getCurrentRoleSafe() {
    return getUserRole();
  }

  const VIEWER_ALLOWED_ACTIONS = ["new-expense", "new-unavailability"];

  function applyQuickActionsRole(role) {
    const isViewer = role === "viewer";
    // Proveedor o rol desconocido: ninguna acción rápida.
    const noActions = !["admin", "manager", "viewer"].includes(role);
    document.querySelectorAll(".quick-action-item").forEach(item => {
      const action = item.getAttribute("data-action");
      item.classList.toggle("hidden", noActions || (isViewer && !VIEWER_ALLOWED_ACTIONS.includes(action)));
    });
  }

  const showOverlay = () => overlay.classList.add("is-visible");
  const hideOverlay = () => overlay.classList.remove("is-visible");
  const showSearch = () => searchOverlay.classList.add("is-visible");
  const hideSearch = () => searchOverlay.classList.remove("is-visible");

  plusBtn.addEventListener("click", async () => {
    applyQuickActionsRole(await getCurrentRoleSafe());
    showOverlay();
  });
  closeBtn.addEventListener("click", hideOverlay);
  searchClose.addEventListener("click", hideSearch);

  overlay.addEventListener("click", (e) => { if (e.target === overlay) hideOverlay(); });

  async function openUnavailabilityFromQuickAction() {
    try {
      const role = await getCurrentRoleSafe();
      const teamId = await getCurrentTeamId({ profileOptions: { allowNull: true } }).catch(() => null);
      const memberId = teamId ? await getCurrentTeamMemberId(teamId).catch(() => null) : null;
      openUnavailabilityModal({ teamId, role, memberId });
    } catch (e) {
      warn("No se pudo abrir el modal de no disponibilidad.", e);
    }
  }

  document.querySelectorAll(".quick-action-item").forEach(item => {
    item.addEventListener("click", () => {
      currentAction = item.getAttribute("data-action");
      hideOverlay();
      if (currentAction === "new-event") openCreateEventModal();
      else if (currentAction === "new-unavailability") openUnavailabilityFromQuickAction();
      else { showSearch(); searchInput.value = ""; searchInput.focus(); renderSearchResults(""); }
    });
  });

  async function renderSearchResults(query) {
    searchResults.innerHTML = '<div class="p-4 text-center"><i class="fa-solid fa-circle-notch fa-spin text-blue-500"></i></div>';
    try {
      const events = await listAllEvents();
      const nQuery = normalizeSearch(query);
      const filtered = events.filter(e =>
        normalizeSearch(e.client_name_legacy || "").includes(nQuery) ||
        normalizeSearch(e.name || "").includes(nQuery)
      ).slice(0, 10);

      if (filtered.length === 0) {
        searchResults.innerHTML = '<div class="p-8 text-center text-slate-400">No se encontraron eventos</div>';
        return;
      }

      searchResults.innerHTML = filtered.map(e => `
        <button class="w-full text-left p-4 bg-slate-50 rounded-2xl border border-slate-100 mb-2 active:bg-slate-100 transition-colors" data-id="${e.id}">
          <div class="font-bold text-slate-900">${e.client_name_legacy || e.name || 'Evento sin nombre'}</div>
          <div class="text-xs text-slate-500">${e.date || 'Sin fecha'}</div>
        </button>
      `).join("");

      searchResults.querySelectorAll("button").forEach(btn => {
        btn.addEventListener("click", () => {
          const eventId = btn.getAttribute("data-id");
          hideSearch();
          if (currentAction === "new-task") {
            openCreateActivityModal({ event_id: eventId });
          } else if (currentAction === "new-expense") {
            openCreateEventExpenseModal({ event_id: eventId });
          } else if (currentAction === "new-income") {
            openCreatePaymentModal({ event_id: eventId });
          }
        });
      });
    } catch (err) { searchResults.innerHTML = '<div class="p-8 text-center text-red-400">Error al buscar eventos</div>'; }
  }

  function searchResultRow({ id, action, icon, iconClass, title, subtitle }) {
    return `
      <button class="w-full text-left p-3 bg-slate-50 rounded-2xl border border-slate-100 mb-2 active:bg-slate-100 transition-colors flex items-center gap-3"
        data-global-id="${escapeHtml(id)}" data-global-action="${escapeHtml(action)}">
        <div class="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${iconClass}"><i class="fa-solid ${icon}"></i></div>
        <div class="min-w-0">
          <div class="font-bold text-slate-900 truncate">${escapeHtml(title)}</div>
          <div class="text-xs text-slate-500 truncate">${escapeHtml(subtitle)}</div>
        </div>
      </button>
    `;
  }

  function searchSectionHeader(label) {
    return `<div class="text-[10px] font-black uppercase tracking-wider text-slate-400 mt-3 mb-1 px-1">${label}</div>`;
  }

  // Busqueda global: eventos (por nombre o fecha), contactos y, si el rol
  // tiene acceso a Mails, tambien correos. Pensada para el icono de lupa de
  // "Hoy" en movil (buscar de todo desde la home), a diferencia de
  // renderSearchResults (arriba), que solo busca eventos para asociarlos a
  // una accion rapida (gasto/ingreso/tarea) ya en marcha.
  //
  // Rendimiento: eventos/emails/rol se cargan UNA sola vez al abrir el
  // buscador (loadGlobalSearchData), no en cada tecla - son las consultas
  // caras (listAllEvents/listAllEmailsWithDetails traen todo). En cada tecla
  // solo se filtra en memoria + se relanza (con debounce) la busqueda de
  // contactos, que si depende del texto y va al servidor.
  let globalSearchDataPromise = null;
  let globalSearchDebounceTimer = null;
  let globalSearchToken = 0;

  function loadGlobalSearchData() {
    globalSearchDataPromise = (async () => {
      const [teamId, role] = await Promise.all([
        getCurrentTeamId({ profileOptions: { allowNull: true } }).catch(() => null),
        getCurrentRoleSafe(),
      ]);
      const canSeeMails = role === "manager" || role === "admin";
      const [events, emails] = await Promise.all([
        listAllEvents().catch(() => []),
        canSeeMails ? listAllEmailsWithDetails().catch(() => []) : Promise.resolve([]),
      ]);
      return { teamId, canSeeMails, events, emails };
    })();
    return globalSearchDataPromise;
  }

  function renderGlobalSearchResults(query, { immediate = false } = {}) {
    clearTimeout(globalSearchDebounceTimer);
    if (immediate) { runGlobalSearch(query); return; }
    globalSearchDebounceTimer = setTimeout(() => runGlobalSearch(query), 250);
  }

  async function runGlobalSearch(query) {
    const token = ++globalSearchToken;
    searchResults.innerHTML = '<div class="p-4 text-center"><i class="fa-solid fa-circle-notch fa-spin text-blue-500"></i></div>';
    const nQuery = normalizeSearch(query);

    try {
      const { teamId, canSeeMails, events, emails } = await (globalSearchDataPromise || loadGlobalSearchData());
      if (token !== globalSearchToken) return; // el usuario siguio escribiendo, esta respuesta ya no aplica

      const contacts = teamId ? await searchContacts(teamId, query, { limit: 8 }).catch(() => []) : [];
      if (token !== globalSearchToken) return;

      const matchedEvents = events.filter(e =>
        normalizeSearch(e.client_name_legacy || "").includes(nQuery) ||
        normalizeSearch(e.name || "").includes(nQuery) ||
        normalizeSearch(e.date || "").includes(nQuery)
      ).slice(0, 6);

      // listAllEmailsWithDetails() devuelve un registro por mensaje, no por
      // hilo: nos quedamos con uno por thread_id (el primero = mas reciente,
      // ya viene ordenado desc) para no repetir el mismo hilo varias veces.
      const seenThreads = new Set();
      const uniqueThreadEmails = canSeeMails ? emails.filter(m => {
        const tid = m.thread_id || m.id;
        if (seenThreads.has(tid)) return false;
        seenThreads.add(tid);
        return true;
      }) : [];

      const matchedEmails = uniqueThreadEmails.filter(m =>
        normalizeSearch(m.subject || "").includes(nQuery) ||
        normalizeSearch(m.contacts?.name || "").includes(nQuery) ||
        normalizeSearch(m.contacts?.email || "").includes(nQuery) ||
        normalizeSearch(m.events?.client_name_legacy || "").includes(nQuery)
      ).slice(0, 6);

      const matchedContacts = (contacts || []).slice(0, 6);

      if (!matchedEvents.length && !matchedContacts.length && !matchedEmails.length) {
        searchResults.innerHTML = '<div class="p-8 text-center text-slate-400">Sin resultados</div>';
        return;
      }

      let html = "";
      if (matchedEvents.length) {
        html += searchSectionHeader("Eventos");
        html += matchedEvents.map(e => searchResultRow({
          id: e.id,
          action: "event",
          icon: "fa-calendar-day",
          iconClass: "bg-blue-100 text-blue-600",
          title: e.client_name_legacy || e.name || "Evento sin nombre",
          subtitle: e.date || "Sin fecha",
        })).join("");
      }
      if (matchedContacts.length) {
        html += searchSectionHeader("Contactos");
        html += matchedContacts.map(c => searchResultRow({
          id: c.id,
          action: "contact",
          icon: "fa-user",
          iconClass: "bg-emerald-100 text-emerald-600",
          title: c.name || "Contacto sin nombre",
          subtitle: c.email || c.phone || "",
        })).join("");
      }
      if (matchedEmails.length) {
        html += searchSectionHeader("Mails");
        html += matchedEmails.map(m => searchResultRow({
          id: m.thread_id || m.id,
          action: "email",
          icon: "fa-envelope",
          iconClass: "bg-amber-100 text-amber-600",
          title: m.subject || "(Sin asunto)",
          subtitle: m.contacts?.name || m.contacts?.email || "",
        })).join("");
      }
      searchResults.innerHTML = html;

      searchResults.querySelectorAll("[data-global-id]").forEach(btn => {
        btn.addEventListener("click", () => {
          const id = btn.getAttribute("data-global-id");
          const action = btn.getAttribute("data-global-action");
          hideSearch();
          if (action === "event") openEditEventModal(id, {});
          else if (action === "contact") openEditContactModal(id, {});
          else if (action === "email") { openThreadOnLoad(id); mountRoute("emailing"); }
        });
      });
    } catch (err) {
      searchResults.innerHTML = '<div class="p-8 text-center text-red-400">Error al buscar</div>';
    }
  }

  function openGlobalSearch() {
    currentAction = "global-search";
    showSearch();
    searchInput.value = "";
    searchInput.placeholder = "Buscar evento, contacto o correo...";
    searchInput.focus();
    loadGlobalSearchData(); // dispara la carga pesada (eventos/emails) una sola vez
    renderGlobalSearchResults("", { immediate: true });
  }

  searchInput.addEventListener("input", (e) => {
    if (currentAction === "global-search") renderGlobalSearchResults(e.target.value);
    else renderSearchResults(e.target.value);
  });

  // Se expone porque el boton que la dispara (icono de lupa en "Hoy") vive en
  // una pagina que el router monta despues de mountQuickActions(); asi la
  // pagina puede llamarla en su propio wireEvents() sin duplicar el overlay.
  window.CRM = window.CRM || {};
  window.CRM.openGlobalSearch = openGlobalSearch;
}

function exposeDebugApi() {
  window.CRM = window.CRM || {};
  window.CRM.store = { getState, actions, bus };
  window.CRM.router = { mountRoute };
  log("Debug API expuesta en window.CRM");
}

function mountExperienceSwitcher() {
  const container = document.getElementById("experience-switcher-container");
  const btnManagement = document.getElementById("btn-mode-management");
  const btnMyEvents = document.getElementById("btn-mode-my-events");

  if (!container || !btnManagement || !btnMyEvents) return;

  const updateSwitcherUI = (mode) => {
    if (mode === "my_events") {
      btnManagement.classList.remove("shadow-sm", "bg-white", "text-slate-800");
      btnManagement.classList.add("text-slate-400", "hover:text-slate-700");
      btnMyEvents.classList.add("shadow-sm", "bg-white", "text-slate-800");
      btnMyEvents.classList.remove("text-slate-400", "hover:text-slate-700");
    } else {
      btnManagement.classList.add("shadow-sm", "bg-white", "text-slate-800");
      btnManagement.classList.remove("text-slate-400", "hover:text-slate-700");
      btnMyEvents.classList.remove("shadow-sm", "bg-white", "text-slate-800");
      btnMyEvents.classList.add("text-slate-400", "hover:text-slate-700");
    }
  };

  // Wire click events
  btnManagement.addEventListener("click", () => {
    setState(d => { d.ui.preferences.experienceMode = "management"; });
  });

  btnMyEvents.addEventListener("click", () => {
    setState(d => { d.ui.preferences.experienceMode = "my_events"; });
  });

  // Listen to store updates
  subscribe((state, prev) => {
    const mode = state.ui?.preferences?.experienceMode || "management";
    const prevMode = prev.ui?.preferences?.experienceMode || "management";
    
    // Si cambia el modo de experiencia
    if (mode !== prevMode) {
      updateSwitcherUI(mode);
      
      // Refrescar menú activo
      refreshActiveNav().catch(() => {});
      
      // Si estamos en una página de manager pero pasamos a modo músico, redirigir a dashboard
      const route = state.ui.route;
      const role = state.team.profile?.role || "viewer";
      const showOnlyViewerPages = role === "viewer" || mode === "my_events";
      if (showOnlyViewerPages && !["dashboard", "settings"].includes(route)) {
        mountRoute("dashboard").catch(() => {});
      }
      
      // Emitir evento para avisar al controlador de Dashboard que refresque su UI
      bus.emit("experienceMode:changed", mode);
    }

    // Mostrar/ocultar switcher según rol y si tiene bolos asignados
    const profile = state.team.profile;
    const role = profile?.role || "viewer";
    const hasGigs = state.team.hasGigs || false;
    
    const showSwitcher = (role === "admin" || role === "manager") && hasGigs;
    container.classList.toggle("hidden", !showSwitcher);
    
    // Asegurar que la UI del switcher esté en sintonía con el modo actual
    updateSwitcherUI(mode);
  });
}

async function boot() {
  await initTailwindCdn();
  await initGlobalsLibs();
  initSupabaseGlobalSafe();
  initStore();
  await loadAndInjectTemplates();
  mountToastsRenderer();
  mountModalHelpers();
  mountSidebarToggle();
  mountPwaForMobile();
  mountQuickActions();
  mountExperienceSwitcher();

  // Mount User Menu
  try {
    const userArea = document.getElementById("user-area");
    if (userArea) mountUserMenu(userArea);
  } catch (e) { warn("Error al montar UserMenu", e); }

  await initRouter();
  exposeDebugApi();

  onAuthStateChange(async (event, session) => {
    const user = session?.user || null;
    // Al cambiar de usuario en la misma pestaña no debe quedar el rol del anterior.
    if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED") clearRoleCache();
    setState(d => { d.auth.user = user; d.auth.session = session; d.auth.status = user ? "signed_in" : "signed_out"; });
    if (user) {
      try {
        const profile = await getProfileByUserId(user.id, { force: true });
        const memberId = await getCurrentTeamMemberId(profile?.team_id).catch(() => null);
        let hasGigs = false;
        if (memberId) {
          const gigs = await getEventIdsByMember(memberId).catch(() => []);
          hasGigs = gigs.length > 0;
        }
        setState(d => {
          d.team.profile = profile;
          d.team.teamId = profile?.team_id;
          d.team.memberId = memberId;
          d.team.hasGigs = hasGigs;
        });
      } catch (e) { warn("Error profile change", e); }
    } else {
      setState(d => {
        d.team.profile = null;
        d.team.teamId = null;
        d.team.memberId = null;
        d.team.hasGigs = false;
      });
    }
  });

  // Global UI Sync: Sidebar Initials
  subscribe((st, prev) => {
    if (st.team.profile !== prev?.team?.profile || (st.auth.user && !prev?.auth?.user)) {
      const user = st.auth.user;
      const profile = st.team.profile;
      if (!user) return;

      const dName = profile?.display_name || user.email.split("@")[0];
      const initials = dName.split(" ").map(n => n[0]).filter(Boolean).join("").substring(0, 2).toUpperCase();
      const el = document.querySelector("#sidebar-settings-initials");
      if (el) el.textContent = initials || "--";
    }
  });
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
else boot();
